<script setup>
import { ref, computed, onMounted, onUnmounted, useTemplateRef, watch } from 'vue';
import { useUserStore } from '@/stores/UserStore';
import { useSessionStore } from '@/stores/SessionStore'
import { useDateHelper, formatLocalized } from "@/composables/useDateHelper";
import { useMediaQuery } from '@/composables/useMediaQuery';
import CurrentConditionsGaugeWindMarker from '@/components/CurrentConditionsGaugeWindMarker.vue';
import Icon from "@/components/icons/Icon.vue";
import useLegacyBridge from "@/composables/useLegacyBridge";
import useConfigurationGlobals from "@/composables/useConfigurationGlobals";
import { intervalToDuration } from 'date-fns';
import { TZDate } from '@date-fns/tz'
import LoaderCircle from './LoaderCircle.vue';

const { $BASEURL } = useConfigurationGlobals();

const ORIGIN = window.location.origin;

const { basicGraph } = useLegacyBridge();
const userStore = useUserStore();
const sessionStore = useSessionStore();
const dateHelper = useDateHelper();
const isDesktop = useMediaQuery('(min-width: 840px)');

const props = defineProps({
  spot: {
    type: Object,
    required: true
  },
  showDistance: {
    type: Boolean,
    default: false
  },
  showLocalTimestamp: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['click', 'register', 'unregister']);

const station = computed(() => props.spot.stations[0]);
const stationDataValues = computed(() => station.value.data_values[0]);
const windDirection = computed(() => stationDataValues.value[props.spot.data_names.indexOf('dir')]);
const windSpeed = computed(() => Math.round(stationDataValues.value[props.spot.data_names.indexOf('avg')]));
const isDown = computed(() => station.value.status_id === 4);
const isNowcast = computed(() => station.value.type === 100 || station.value.type === 101);

const rowContainerElement = useTemplateRef('row-container-element');
const obsGraphContainer = useTemplateRef('obs-graph-container');

const obsDescription = computed(() => {
  const dataNames = props.spot.data_names;
  
  let desc = `<span class="jwx-spot-list-row__wind-description">${stationDataValues.value[dataNames.indexOf('wind_desc')]}</span>`;
  const dir = stationDataValues.value[dataNames.indexOf('dir')];
  if (dir != null) desc += ` (${dir}&deg;)`;
  
  const atemp = stationDataValues.value[dataNames.indexOf('atemp')];
  if (atemp != null) desc += ` ${Math.round(atemp)}&deg;${userStore.units.temp.toUpperCase()}`;

  return desc;
});

const relativeTime = ref('');
let timeInterval = null;

const updateRelativeTime = () => {
  const timestamp = stationDataValues.value[props.spot.data_names.indexOf('timestamp')];
  const tzOffsetString = dateHelper.formatOffsetToString(props.spot.tz_offset);

  if (timestamp) {
    const isoTimestamp = dateHelper.datetimeToISOFormat(timestamp, tzOffsetString);
    const durations = intervalToDuration({ start: isoTimestamp, end: Date.now()});
    const duration = { amount: 0, label: '' }
    
    if (Object.hasOwnProperty.call(durations, 'weeks')) {
      duration.amount = durations['weeks'];
      duration.label = 'week';
    }
    else if (Object.hasOwnProperty.call(durations, 'days')) {
      duration.amount = durations['days'];
      duration.label = 'day';
    }
    else if (Object.hasOwnProperty.call(durations, 'hours')) {
      duration.amount = durations['hours'];
      duration.label = 'hour';
    }
    else if (Object.hasOwnProperty.call(durations, 'minutes')) {
      duration.amount = durations['minutes'];
      duration.label = 'min';
    }
    else if (Object.hasOwnProperty.call(durations, 'seconds')) {
      duration.amount = durations['seconds'];
      duration.label = 'sec';
    }

    const label = `${duration.label}${duration.amount > 1 ? 's' : ''}`
    relativeTime.value = `${duration.amount} ${label} ago`;
 
    return;
  }

  relativeTime.value = 'N/A';
};

const localTime = computed(() => {
  try {
    return formatLocalized(new TZDate(new Date(), props.spot.tz_name), 'p')
  } catch (e) {
    // fallback if timeZone is invalid or not supported
    return 'N/A';
  }
});

const fxGraphUrl = computed(() => {
  const spotInfo = props.spot.spot_id > 0 ? `&spot_id=${props.spot.spot_id}` : `&lat=${props.spot.lat}&lon=${props.spot.lon}`;
  const queryString = `
    wf_token=${sessionStore.token}&
    format=raw&
    image_format=png&
    type=bar&
    station_id=${station.value.station_id}&
    hide_arrows=true&
    units_wind=${userStore.units.wind}&
    graph_width=2500&
    graph_height=600&
    scale=.1&
    fields=1
    ${spotInfo}
  `;
  
  return `${$BASEURL}/graph/getGraph?${queryString.replace(/\s+/g, '')}`;
});

const linkUrl = computed(() => {
  const id = props.spot.spot_id || props.spot.fav_spot_id;
  const type = props.spot.spot_id ? 'spot' : 'favspot'

  return `${ORIGIN}/${type}/${id}`;
})

const windBarWidth = computed(() => {
  const avg = stationDataValues.value[props.spot.data_names.indexOf('avg')];

  if (avg == null) return 0;

  const cappedAvg = avg > 30 ? 30 : avg;
  return (cappedAvg / 30) * 100;
});

let panelObserver = null;

const loadGraphs = () => {
  if (obsGraphContainer.value) {
    const graphOptions = {
      extents: [-4, 2],
      type: 'mini',
      chartSpacing: [0, 0, 0, 2],
      chartMargin: [3, 3, 14, 17],
      yAxisOffset: -12,
      xAxisTickLength: 5,
      xAxisTickDistance: 12,

      spotId: props.spot.spot_id,
      favSpotId: props.spot.fav_spot_id,
      miniGraphClicked: () => emit('click', props.spot)
    };

    basicGraph(obsGraphContainer.value, graphOptions);
  }
};

const handlePanelIndicatorClick = (target) => {
  const panel = rowContainerElement.value.querySelector(`[data-panel=${target}]`);
  panel.scrollIntoView({ behavior: "smooth", container: "nearest"})
}

const observePanels = () => {
  panelObserver?.disconnect();
  panelObserver = null;

  const rowEl = rowContainerElement.value;
  if (!rowEl || isDesktop.value) return;

  panelObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.target?.dataset?.panel) return;
      rowEl.parentElement.querySelector(`[data-panel-indicator=${entry.target.dataset.panel}]`).dataset.isVisible = entry.isIntersecting
    });
  }, { root: rowEl, threshold: 0.75 });

  rowEl.querySelectorAll('[data-panel]').forEach((panel) => panelObserver.observe(panel));
}

onMounted(() => {
  updateRelativeTime();
  timeInterval = setInterval(updateRelativeTime, 30000);
  emit('register', { el: rowContainerElement.value, loadGraphs });
});

onUnmounted(() => {
  emit('unregister', rowContainerElement.value);
  panelObserver?.disconnect();
  if (timeInterval) clearInterval(timeInterval);
});

watch([rowContainerElement, isDesktop], observePanels, { immediate: true });

watch(() => props.spot, () => loadGraphs())
</script>

<template>
    <a 
      :href="linkUrl" 
      ref="row-container-element" 
      class="jwx-spot-list-row"
    >
      <div
        class="jwx-spot-list-row__main"
        :data-panel="`main-panel-${props.spot.spot_id}`"
      >
        <div v-if="isDown" class="jwx-spot-list-row__obs-graph">
          <div class="jwx-spot-list-row__down-station">
            <span class="jwx-spot-list-row__down-station-text">Station Down</span>
          </div>
        </div>
        <div v-else ref="obs-graph-container" class="jwx-spot-list-row__obs-graph">
          <LoaderCircle
            :showText="false"
            style="--jwx-circle-loader-size: 2.25rem; --jwx-circle-loader-mask-size: .7rem;"
          />
        </div>
        <div
          class="jwx-spot-list-row__wind-marker"
          :class="{'jwx-p-right-base': isDown}"
          data-mini="true"
        >
          <CurrentConditionsGaugeWindMarker
            :iconColor="props.spot.icon_color"
            :iconType="props.spot.icon_type"
            :windDir="windDirection"
            :status="station.status_id"
            :isNowcast="isNowcast"
            :upgrade="[]"
          />
          <p v-if="!isDown" class="jwx-spot-list-row__wind-speed">
            {{ windSpeed }}
          </p>
        </div>
        <div class="jwx-spot-list-row__spot-info">
          <p class="jwx-spot-list-row__spot-name" v-html="props.spot.name"></p>
          <p class="jwx-spot-list-row__obs-description" v-html="obsDescription"></p>
          <div v-if="!isDown" class="jwx-spot-list-row__wind-bar">
            <div class="jwx-spot-list-row__wind-bar-fill" :style="{ width: windBarWidth + '%' }"></div>
          </div>
        </div>
        <div class="jwx-spot-list-row__meta">
          <p class="jwx-spot-list-row__relative-time" v-html="relativeTime"></p>
          <p v-if="props.showLocalTimestamp" class="jwx-spot-list-row__local-time">{{ localTime }}</p>
          <p v-if="props.showDistance" class="jwx-spot-list-row__distance">{{ Math.round(props.spot.distance )}} {{ userStore.units.distance }} away</p>
        </div>
      </div>
      <div
        class="jwx-spot-list-row__fx-graph"
        :data-panel="`fx-graph-panel-${props.spot.spot_id}`"
      >
        <img :src="fxGraphUrl" :alt="`${props.spot.name} wind forecast graph`" class="jwx-spot-list-row__fx-graph-img" loading="lazy" />
      </div>
      <div class="jwx-spot-list-row__nav-indicator">
        <Icon name="chevron-right" />
      </div>
      <div v-if="!isDesktop" class="jwx-spot-list-row__panel-indicator">
        <button 
          @click.prevent="handlePanelIndicatorClick(`main-panel-${props.spot.spot_id}`)"
          :data-panel-indicator="`main-panel-${props.spot.spot_id}`" 
          class="jwx-spot-list-row__panel-marker"
          ></button>
          <button 
          @click.prevent="handlePanelIndicatorClick(`fx-graph-panel-${props.spot.spot_id}`)"
          :data-panel-indicator="`fx-graph-panel-${props.spot.spot_id}`" 
          class="jwx-spot-list-row__panel-marker"
        ></button>
      </div>
    </a>
</template>
